import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// Import user rights context
import { useUserRights } from '../../../context/UserRightsContext';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';

const AppConfig = () => {
  // Use user rights context
  const { hasPermission, isHarmonateUser } = useUserRights();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newConfig, setNewConfig] = useState({
    appConfigP: '',
    appValue1: ''
  });
  
  const [editConfig, setEditConfig] = useState({
    id: '',
    appConfigP: '',
    appValue1: '',
    tenantId: ''
  });
  
  const [deleteConfig, setDeleteConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAppConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const getTenantId = () => {
        const token = localStorage.getItem('tenant_id');
        return token;
      };
      const tenantId = getTenantId();
      
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/app-config/tenant/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error(`Failed to fetch app configs: ${response.status} ${response.statusText}`);
      
      const data = await response.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddConfig = async (e) => {
    e.preventDefault();
    if (!newConfig.appConfigP || !newConfig.appValue1) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // Updated to match backend endpoint: /conductor/app-config
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/app-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appConfigP: newConfig.appConfigP,
          appValue1: newConfig.appValue1,
          tenantId: tenantId
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add configuration');
      }
      
      setAddDialogOpen(false);
      setNewConfig({ appConfigP: '', appValue1: '' });
      fetchAppConfigs();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleEditClick = (config) => {
    // Check if user has edit permission for App Config function
    if (!isHarmonateUser && !hasPermission('App Config', 'edit')) {
      alert('You do not have permission to edit app configurations');
      return;
    }

    setEditConfig({
      id: config.id,
      appConfigP: config.appConfigP,
      appValue1: config.appValue1,
      tenantId: config.tenantId
    });
    setEditDialogOpen(true);
  };
  
  const handleUpdateConfig = async () => {
    if (!editConfig.id || !editConfig.appConfigP || !editConfig.appValue1) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Updated to match backend endpoint: /conductor/app-config/{id}
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/app-config/${editConfig.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: editConfig.id,
            appConfigP: editConfig.appConfigP,
            appValue1: editConfig.appValue1,
            tenantId: editConfig.tenantId
          }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update configuration');
      }
      
      setEditDialogOpen(false);
      fetchAppConfigs();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteClick = (config) => {
    setDeleteConfig(config);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfig = async () => {
    if (!deleteConfig) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // You'll need to check if you have a DELETE endpoint that matches this pattern
      // If not, you might need to use: /conductor/app-config/{id}
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/app-config/${deleteConfig.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete configuration');
      }
      
      setDeleteDialogOpen(false);
      setDeleteConfig(null);
      fetchAppConfigs();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAppConfigs();
  }, []);

  const columns = [
    { 
      field: 'appConfigP', 
      headerName: 'Configuration Key', 
      flex: 1,
      editable: false // Changed to false since you're using dialog for editing
    },
    { 
      field: 'appValue1', 
      headerName: 'Value', 
      flex: 1,
      editable: false // Changed to false since you're using dialog for editing
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(params.row);
            }}
            disabled={!isHarmonateUser && !hasPermission('App Config', 'edit')}
            sx={{
              ...harmonateGridActionIconSx,
              ...((!isHarmonateUser && !hasPermission('App Config', 'edit')) && {
                color: ht.fadedText,
                '&:hover': { bgcolor: 'transparent', color: ht.fadedText },
              }),
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
            sx={harmonateGridActionIconSx}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <Typography sx={{ color: ht.white, mb: 2 }}>Error: {error}</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={fetchAppConfigs}
          sx={harmonateUploadOutlinedSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Application Configuration
        </Typography>
        <Tooltip title="Add Config">
          <span>
            <IconButton
              onClick={() => {
                if (!isHarmonateUser && !hasPermission('App Config', 'add')) {
                  alert('You do not have permission to add app configurations');
                  return;
                }
                setAddDialogOpen(true);
              }}
              disabled={!isHarmonateUser && !hasPermission('App Config', 'add')}
              sx={harmonatePlainIconButtonSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          columnResizeMode="onEnd"
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          resizable
          rows={configs}
          columns={columns}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          rowHeight={40}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
          components={{
            NoRowsOverlay: () => (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No configuration found
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={fetchAppConfigs}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Add Config Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add New Configuration</DialogTitle>
        <form onSubmit={handleAddConfig}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              label="Configuration Key"
              value={newConfig.appConfigP}
              onChange={(e) => setNewConfig({...newConfig, appConfigP: e.target.value})}
              required
              fullWidth
              size="small"
              placeholder="e.g., DateFormat, AmountFormat"
            />
            <TextField
              label="Value"
              value={newConfig.appValue1}
              onChange={(e) => setNewConfig({...newConfig, appValue1: e.target.value})}
              required
              fullWidth
              size="small"
              placeholder="e.g., DD/MM/YYYY, #,###,###,##0.00"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setAddDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={harmonateProceedButtonSx}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Edit Configuration</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            label="Configuration Key"
            value={editConfig.appConfigP}
            onChange={(e) => setEditConfig({...editConfig, appConfigP: e.target.value})}
            required
            fullWidth
            size="small"
            disabled={true}
          />
          <TextField
            label="Value"
            value={editConfig.appValue1}
            onChange={(e) => setEditConfig({...editConfig, appValue1: e.target.value})}
            required
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateConfig} 
            variant="contained" 
            disabled={submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete the configuration for &quot;{deleteConfig?.appConfigP}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={submitting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfig} 
            variant="contained"
            disabled={submitting}
            sx={harmonateAbortButtonSx}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppConfig;